import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { RefreshCw, CheckCircle, XCircle, Clock, AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface ExecutionLog {
  id: string;
  created_at: string;
  channel: string;
  status: string;
  error_type?: string;
  error_message?: string;
  metadata?: any;
  confidence_score?: number;
  session_id?: string;
}

export default function FunctionExecutionLogs() {
  const [logs, setLogs] = useState<ExecutionLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    loadLogs();
    
    if (autoRefresh) {
      const interval = setInterval(loadLogs, 3000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const loadLogs = async () => {
    try {
      const { data, error } = await supabase
        .from('ai_usage_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      if (data) setLogs(data as ExecutionLog[]);
    } catch (error: any) {
      console.error('Failed to load execution logs:', error);
      toast.error('Failed to load execution logs');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'processing':
        return <Clock className="h-4 w-4 text-yellow-500 animate-spin" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500/10 text-green-500';
      case 'error':
        return 'bg-red-500/10 text-red-500';
      case 'processing':
        return 'bg-yellow-500/10 text-yellow-500';
      default:
        return 'bg-gray-500/10 text-gray-500';
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Function Execution Logs</CardTitle>
            <CardDescription>Real-time monitoring of all agent function calls</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
            >
              {autoRefresh ? 'Pause' : 'Resume'} Auto-refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={loadLogs}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[600px] pr-4">
          <div className="space-y-3">
            {logs.length === 0 && !loading && (
              <div className="text-center py-12 text-muted-foreground">
                <p>No execution logs found</p>
                <p className="text-sm mt-2">Agent function calls will appear here in real-time</p>
              </div>
            )}
            
            {logs.map((log) => {
              const isAgentChat = log.channel === 'agent_control_chat';
              return (
                <div
                  key={log.id}
                  className={`border rounded-lg p-4 hover:bg-muted/50 transition-colors ${
                    isAgentChat ? 'border-primary/50 bg-primary/5' : ''
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(log.status)}
                      <Badge 
                        variant={isAgentChat ? "default" : "outline"} 
                        className="font-mono text-xs"
                      >
                        {log.channel}
                      </Badge>
                      <Badge className={getStatusColor(log.status)}>
                        {log.status}
                      </Badge>
                      {log.metadata?.model && (
                        <Badge variant="secondary" className="text-xs">
                          {log.metadata.model}
                        </Badge>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {new Date(log.created_at).toLocaleString()}
                    </span>
                  </div>

                  {log.error_message && (
                    <div className="mt-2 bg-red-500/10 border border-red-500/20 rounded p-2">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="destructive" className="text-xs">
                          {log.error_type || 'ERROR'}
                        </Badge>
                        {log.metadata?.code && (
                          <Badge variant="outline" className="text-xs">
                            {log.metadata.code}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-red-500 font-mono">{log.error_message}</p>
                    </div>
                  )}

                {log.confidence_score !== null && log.confidence_score !== undefined && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-muted-foreground">Confidence Score</span>
                      <span className="font-medium">{(log.confidence_score * 100).toFixed(1)}%</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary transition-all"
                        style={{ width: `${log.confidence_score * 100}%` }}
                      />
                    </div>
                  </div>
                )}

                {log.session_id && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    Session: <span className="font-mono">{log.session_id}</span>
                  </div>
                )}

                  {log.metadata && Object.keys(log.metadata).length > 0 && (
                    <details className="mt-2">
                      <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                        View metadata
                      </summary>
                      <pre className="mt-2 text-xs bg-muted p-2 rounded overflow-x-auto">
                        {JSON.stringify(log.metadata, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
